import { useState } from 'react';
import { Linking, Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Circle, Path, Rect, Line } from 'react-native-svg';
import { colors, radii, spacing } from '../../../constants/theme';

// ── Icons ────────────────────────────────────────────────────────────────────

type IconProps = { size?: number; color?: string };

function ImageCardIcon({ size = 24, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={3} y={4} width={18} height={16} rx={2} stroke={color} strokeWidth={1.8} />
      <Circle cx={8.5} cy={9.5} r={1.5} stroke={color} strokeWidth={1.6} />
      <Path d="M3 15l5-4 4 4 3-3 6 6" stroke={color} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function RepeatIcon({ size = 24, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M17 2l4 4-4 4" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M3 11V9a4 4 0 0 1 4-4h14" stroke={color} strokeWidth={1.9} strokeLinecap="round" />
      <Path d="M7 22l-4-4 4-4" stroke={color} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />
      <Path d="M21 13v2a4 4 0 0 1-4 4H3" stroke={color} strokeWidth={1.9} strokeLinecap="round" />
    </Svg>
  );
}

function CheckSquareIcon({ size = 24, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={3} y={3} width={18} height={18} rx={3} stroke={color} strokeWidth={1.8} />
      <Path d="M7.5 12l3 3 6-6" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

function SearchIcon({ size = 24, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Circle cx={11} cy={11} r={7} stroke={color} strokeWidth={1.9} />
      <Line x1={16.5} y1={16.5} x2={21} y2={21} stroke={color} strokeWidth={2} strokeLinecap="round" />
      <Path d="M8 11h6M11 8v6" stroke={color} strokeWidth={1.7} strokeLinecap="round" />
    </Svg>
  );
}

function ListIcon({ size = 24, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M8 6h13M8 12h13M8 18h13" stroke={color} strokeWidth={1.9} strokeLinecap="round" />
      <Circle cx={3.5} cy={6} r={1.2} fill={color} />
      <Circle cx={3.5} cy={12} r={1.2} fill={color} />
      <Circle cx={3.5} cy={18} r={1.2} fill={color} />
    </Svg>
  );
}

function WhatsAppIcon({ size = 16, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.37 5.07L2 22l5.12-1.34A9.94 9.94 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2Z"
        stroke={color}
        strokeWidth={1.7}
        strokeLinejoin="round"
      />
      <Path
        d="M8.5 9.5c.5 1 1.5 3 3.5 4s3-.5 3-.5-.5-1.5-1-1.5-1 .5-1 .5-2-1-3-3c0 0 .5-.5.5-1S9 7.5 8.5 7.5 8 9 8.5 9.5Z"
        fill={color}
      />
    </Svg>
  );
}

function CopyIcon({ size = 16, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={9} y={9} width={12} height={12} rx={2} stroke={color} strokeWidth={1.7} />
      <Path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke={color} strokeWidth={1.7} strokeLinecap="round" />
    </Svg>
  );
}

// ── Types ────────────────────────────────────────────────────────────────────

type ListOption = {
  Icon: React.ComponentType<IconProps>;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  buildText: () => string;
};

type ShareOptionsSheetProps = {
  visible: boolean;
  onClose: () => void;
  onShareCard: () => void;
  buildRepeatedText: () => string;
  buildOwnedText: () => string;
  buildMissingText: () => string;
  buildAllText: () => string;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

async function sendToWhatsApp(text: string) {
  const url = Platform.OS === 'web'
    ? `https://web.whatsapp.com/send?text=${encodeURIComponent(text)}`
    : `whatsapp://send?text=${encodeURIComponent(text)}`;

  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const supported = await Linking.canOpenURL(url);
  if (supported) {
    await Linking.openURL(url);
  } else {
    const { Share } = await import('react-native');
    await Share.share({ message: text });
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function ShareOptionsSheet({
  visible,
  onClose,
  onShareCard,
  buildRepeatedText,
  buildOwnedText,
  buildMissingText,
  buildAllText,
}: ShareOptionsSheetProps) {
  const insets = useSafeAreaInsets();
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = async (key: string, text: string) => {
    await Clipboard.setStringAsync(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleWhatsApp = async (text: string) => {
    onClose();
    await sendToWhatsApp(text);
  };

  const listOptions: ListOption[] = [
    {
      Icon: RepeatIcon,
      iconColor: '#22C55E',
      iconBg: 'rgba(34,197,94,0.15)',
      title: 'Mis repetidas',
      description: 'Figus que te sobran',
      buildText: buildRepeatedText,
    },
    {
      Icon: CheckSquareIcon,
      iconColor: '#60A5FA',
      iconBg: 'rgba(96,165,250,0.15)',
      title: 'Las que tengo',
      description: 'Todas las que ya pegaste',
      buildText: buildOwnedText,
    },
    {
      Icon: SearchIcon,
      iconColor: '#F59E0B',
      iconBg: 'rgba(245,158,11,0.15)',
      title: 'Las que me faltan',
      description: 'Lista de faltantes',
      buildText: buildMissingText,
    },
    {
      Icon: ListIcon,
      iconColor: '#A78BFA',
      iconBg: 'rgba(167,139,250,0.15)',
      title: 'Todo junto',
      description: 'Tenidas, repetidas y faltantes',
      buildText: buildAllText,
    },
  ];

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={[styles.sheet, { paddingBottom: insets.bottom + spacing.md }]}>
          <View style={styles.handle} />
          <Text style={styles.title}>¿Qué querés compartir?</Text>

          {/* Opción imagen — press único */}
          <Pressable
            onPress={() => { onClose(); onShareCard(); }}
            style={({ pressed }) => [styles.option, pressed && styles.pressed]}
          >
            <View style={[styles.iconWrap, { backgroundColor: 'rgba(255,215,0,0.15)' }]}>
              <ImageCardIcon size={22} color="#FFD700" />
            </View>
            <View style={styles.optionText}>
              <Text style={styles.optionTitle}>Compartir mi figurita</Text>
              <Text style={styles.optionDesc}>Imagen con tu progreso del álbum</Text>
            </View>
          </Pressable>

          {/* Opciones de lista — con botones Copiar y WhatsApp */}
          {listOptions.map((opt) => (
            <View key={opt.title} style={styles.option}>
              <View style={[styles.iconWrap, { backgroundColor: opt.iconBg }]}>
                <opt.Icon size={22} color={opt.iconColor} />
              </View>
              <View style={styles.optionText}>
                <Text style={styles.optionTitle}>{opt.title}</Text>
                <Text style={styles.optionDesc}>{opt.description}</Text>
                <View style={styles.actionRow}>
                  <Pressable
                    onPress={() => handleWhatsApp(opt.buildText())}
                    style={({ pressed }) => [styles.actionBtn, styles.actionWhatsapp, pressed && styles.pressed]}
                  >
                    <WhatsAppIcon size={14} color="#fff" />
                    <Text style={styles.actionBtnText}>WhatsApp</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => handleCopy(opt.title, opt.buildText())}
                    style={({ pressed }) => [styles.actionBtn, styles.actionCopy, pressed && styles.pressed]}
                  >
                    {copied === opt.title
                      ? <Text style={styles.actionCopyText}>✓ Copiado</Text>
                      : <>
                          <CopyIcon size={14} color={colors.text} />
                          <Text style={styles.actionCopyText}>Copiar</Text>
                        </>
                    }
                  </Pressable>
                </View>
              </View>
            </View>
          ))}

          <Pressable
            onPress={onClose}
            style={({ pressed }) => [styles.cancel, pressed && styles.pressed]}
          >
            <Text style={styles.cancelText}>Cancelar</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: radii.lg,
    borderTopRightRadius: radii.lg,
    paddingTop: spacing.sm,
    paddingHorizontal: spacing.lg,
    gap: spacing.xs,
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    alignSelf: 'center',
    marginBottom: spacing.sm,
  },
  title: {
    color: colors.textMuted,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  optionText: {
    flex: 1,
  },
  optionTitle: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  optionDesc: {
    color: colors.textMuted,
    fontSize: 12,
    marginTop: 1,
  },
  actionRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.sm,
  },
  actionWhatsapp: {
    backgroundColor: '#25D366',
  },
  actionCopy: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  actionBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },
  actionCopyText: {
    color: colors.text,
    fontSize: 13,
    fontWeight: '700',
  },
  cancel: {
    alignItems: 'center',
    paddingVertical: spacing.md,
    marginTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  cancelText: {
    color: colors.primary,
    fontSize: 15,
    fontWeight: '800',
  },
  pressed: {
    opacity: 0.7,
  },
});
